import { type MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAtomValue } from "jotai";
import { AddFeature, Feature, useFeatureTernary } from "@viagogo/feature";
import { FeatureSetEnum } from "src/shared/constants/FeatureSetEnum";
import { PE_KEYS } from "src/shared/constants/PageEventKeys";
import { PE_TYPE } from "src/shared/constants/PageEventTypes";
import { useAppContext } from "src/shared/context/AppContext";
import { getLocationCookieOptions, LocationContext, useLocationContext } from "src/shared/context/location-context";
import { EventViewTypes } from "src/shared/enums/enums";
import { useSearchParams } from "src/shared/hooks/useSearchParams";
import { useStorePageEvent } from "src/shared/hooks/useStorePageEvent";
import { useWindowLocation } from "src/shared/hooks/useWindowLocation";
import { cookieStore } from "src/shared/stores";
import { sendPageEventImmediately } from "src/shared/utils/pageevents";
import { type Location, LocationSourceType } from "src/types/shared/cookies";
import { AipBottomsheetFeature } from "src/website-apps/responsive/stubhub-performer/components/aip-bottomsheet/AipBottomsheetFeature";
import { Body } from "src/website-apps/responsive/stubhub-performer/components/page-body";
import { Header } from "src/website-apps/responsive/stubhub-performer/components/page-header";
import { PerformerLayout } from "src/website-apps/responsive/stubhub-performer/components/portal/PerformerLayout";
import { PostHogSessionReplay } from "src/website-apps/responsive/stubhub-performer/components/posthog-session-replay/PostHogSessionReplay";
import { usePerformerPageModelData } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerPageModelData";
import { usePerformerToasts } from "src/website-apps/responsive/stubhub-performer/hooks/usePerformerToasts";
import { LazyFooter } from "src/website-apps/shared/components/footer";
import { ENTITY_TYPES } from "src/website-apps/shared/components/grid/favorites-fs/constants";
import { FavoritesProvider } from "src/website-apps/shared/components/grid/favorites-fs/FavoritesContext";
import { HeroBannerFavoritesDataContext } from "src/website-apps/shared/components/hero-banner";
import { RelistBanner as STHRelistBanner } from "src/website-apps/shared/components/sell-one-click-relist-banner";
import { PrimaryRadius, PrimaryRadiusInMeters } from "src/website-apps/shared/constants/grid-radius";
import { DateContext, useDateCookieOptions, useDatesContext } from "src/website-apps/shared/context/date-context";
import { EventsGridContext } from "src/website-apps/shared/context/events-grid-context";
import { useGridsReducer } from "src/website-apps/shared/hooks/useGridsReducer";
import { useStableNow } from "src/website-apps/shared/hooks/useStableNow";
import { isConfirmLocationModalNotVisibleAtom } from "src/website-apps/shared/utils/confirmLocationVisibilityAtom";
import { getDateQueryParams } from "src/website-apps/shared/utils/getDateQueryParams";
import { getFilterQueryParams } from "src/website-apps/shared/utils/getFilterQueryParams";
import {
  getCalendarFromSurface,
  getDefaultHomeAwayFilterTypeForSurface,
  getDisplayedPerformerGridEntries,
  getPerformerGridReducerSlots,
  getPerformerResidencyContext,
  isCalendarSurface,
} from "src/website-apps/shared/utils/performerGridSurface";
import { getRecentlyViewedEventsFromCookies } from "src/website-apps/shared/utils/recently-viewed";
import { sendLocationChangeUAE, sendPerformerLandingUAE } from "../utils/analyticsUtils";
import { buildLocationOverride, resolveInitialFavoritesStateFromPerformerPageModel } from "./utils";

export const App = () => {
  const appContext = useAppContext();
  const { env } = appContext;
  const storePageEvent = useStorePageEvent();
  const model = usePerformerPageModelData();
  const dateCookieOptions = useDateCookieOptions();
  const datesContext = useDatesContext(false, dateCookieOptions);
  const tabVisibleLogged: MutableRefObject<boolean> = useRef(false);
  const [initialPageTimeEventStart] = useState(Date.now);
  const pageTimeEventStartTime: MutableRefObject<number> = useRef(initialPageTimeEventStart);
  const pageTimeEventCount: MutableRefObject<number> = useRef(0);
  const previousLocationRef: MutableRefObject<Location | null> = useRef(null);
  const hasInitialLocationBeenSet: MutableRefObject<boolean> = useRef(false);

  const { aipBottomsheet, isReturningGoogleUser } = usePerformerToasts();

  const locationOverride: Location | undefined = useMemo(
    () => buildLocationOverride(model.locationOverride?.city),
    [model.locationOverride],
  );

  const cookieOptions = useMemo(() => getLocationCookieOptions(appContext), [appContext]);
  const locationContext = useLocationContext(cookieOptions, locationOverride);

  const [location] = useMemo(() => locationContext, [locationContext]);

  const isConfirmLocationModalNotVisible = useAtomValue(isConfirmLocationModalNotVisibleAtom);

  useEffect(() => {
    const sendPageTime = () => {
      sendPageEventImmediately(
        PE_TYPE.LOGGING,
        PE_KEYS.PERFORMER_PAGE_TIME,
        `${String(pageTimeEventCount.current)}_${String(Date.now() - pageTimeEventStartTime.current)}`,
        appContext,
        env,
      );
      pageTimeEventCount.current += 1;
    };

    const sendTabVisible = () => {
      if (tabVisibleLogged.current) {
        return;
      }

      tabVisibleLogged.current = true;
      sendPageEventImmediately(PE_TYPE.LOGGING, PE_KEYS.PERFORMER_TAB_VISIBLE, "", appContext, env);
    };

    const handlePageHide = () => {
      sendPageTime();
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        sendPageTime();
      } else {
        pageTimeEventStartTime.current = Date.now();
        sendTabVisible();
      }
    };

    const handleFocus = () => {
      sendTabVisible();
    };

    const handleBeforeUnload = () => {
      sendPageTime();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange, { passive: true });
    window.addEventListener("focus", handleFocus, { passive: true });
    window.addEventListener("pagehide", handlePageHide, { passive: true });
    window.addEventListener("beforeunload", handleBeforeUnload, { passive: true });

    const timeoutId = setTimeout(() => {
      if (document.visibilityState === "visible" || document.hasFocus()) {
        sendTabVisible();
      }
    }, 100);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("pagehide", handlePageHide);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      clearTimeout(timeoutId);
    };
  }, [storePageEvent, appContext, env]);

  const { search } = useWindowLocation();
  const now = useStableNow();
  const execDateWithExpiration = useFeatureTernary(FeatureSetEnum.EventListExpireDateFilter);
  const dates = useMemo(
    () =>
      getDateQueryParams(
        env,
        search,
        now,
        !!execDateWithExpiration({
          control: () => false,
          variant: () => true,
        }),
        dateCookieOptions,
      ),
    [env, search, now, execDateWithExpiration, dateCookieOptions],
  );

  const isCalendarDefaultView = isCalendarSurface(model.performerGridSurface);
  const defaultHomeAwayFilterType = getDefaultHomeAwayFilterTypeForSurface(model.performerGridSurface);
  const canParticipateMultiSelectRounds = useCallback(
    () => !isCalendarDefaultView && (model.availableFilters?.tournamentRounds?.length ?? 0) > 0,
    [isCalendarDefaultView, model.availableFilters?.tournamentRounds?.length],
  );
  const execMultiSelectRounds = useFeatureTernary(
    FeatureSetEnum.TMPerformerMultiSelectRounds,
    canParticipateMultiSelectRounds,
  );
  const isMultiSelectRoundsEnabled = useMemo(
    () =>
      execMultiSelectRounds({
        control: () => false,
        variant: () => true,
        record: false,
      }) ?? false,
    [execMultiSelectRounds],
  );

  const defaultFilters = useMemo(
    () => ({
      ...(isCalendarDefaultView && { eventViewType: EventViewTypes.CALENDAR }),
      homeAwayFilterType: defaultHomeAwayFilterType,
      sortBy: model.sortedBy,
    }),
    [defaultHomeAwayFilterType, isCalendarDefaultView, model.sortedBy],
  );
  const filters = useMemo(
    () =>
      getFilterQueryParams(search, defaultFilters, {
        parseSingleTournamentRoundAsMultiSelect: isMultiSelectRoundsEnabled,
      }),
    [search, defaultFilters, isMultiSelectRoundsEnabled],
  );

  const [userHasVisitedBefore, setUserHasVisitedBefore] = useState(false);
  useEffect(() => {
    if (cookieStore.get("ulv", env)) {
      setUserHasVisitedBefore(true);
    }

    cookieStore.add("ulv", Date.now().toString(), env, false);
  }, [env]);
  const recentlyViewedEventsArray = useMemo(() => getRecentlyViewedEventsFromCookies(env), [env]);

  const overrideOpponentCategoryId =
    model.opponentFilterOverride && !filters.opponentCategoryId ? model.opponentFilterOverride : null;

  // TODO 1) update backend enum: add default option 0 2) set this default to 0 in getFilterQueryParams and useGridsReducer 3) fix F1TypeSelector
  const searchParams = useSearchParams();
  const queryParamRaceType = searchParams["raceType"];
  const f1FilterDefault = queryParamRaceType ? Math.trunc(Number(queryParamRaceType)) : null;

  const reducerOptions = useMemo(() => {
    const seoLocation: Location | null = model.seoCity
      ? {
          n: model.seoCity.cityName ?? "?",
          ct: model.seoCity.countryName,
          lt: model.seoCity.latitude ?? 0,
          lg: model.seoCity.longitude ?? 0,
          src: LocationSourceType.SEO,
        }
      : null;

    const { primaryGrid, restGrid } = getPerformerGridReducerSlots(model.performerGridSurface);

    return {
      primaryGrid,
      primaryFromRadius: 0,
      primaryToRadius: PrimaryRadiusInMeters,
      restGrid,
      restFromRadius: PrimaryRadiusInMeters,
      restToRadius: null,
      method: "GetFilteredEvents",
      categoryId: model.categorySummary.categoryId,
      cityId: model.seoCity?.cityId,
      location: seoLocation ?? location,
      country: filters.country,
      tour: filters.tour,
      dates,
      sortBy: filters.sortBy,
      gridFilterType: filters.gridFilterType,
      venueIdFilterType: filters.venueIdFilterType ?? 0,
      nearbyGridRadius: PrimaryRadius,
      eventViewType: filters.eventViewType,
      calendarEvents: getCalendarFromSurface(model.performerGridSurface),
      paidSearchLocation: model.paidSearchQueryCity,
      paidSearchVenueId: model.paidSearchQueryVenueId,
      recentlyViewedEvents: recentlyViewedEventsArray,
      raceType: f1FilterDefault,
      opponentCategoryId: overrideOpponentCategoryId ?? filters.opponentCategoryId,
      homeAwayFilterType: filters.homeAwayFilterType,
      eventBooleanFilters: filters.eventBooleanFilters,
      passLengthType: filters.passLengthType,
      timeOfDayType: filters.timeOfDayType,
      pinnedEvent: model.pinnedEvent,
      medianPriceBucketFilter: filters.medianPriceBucketFilter,
      tournamentRound: filters.tournamentRound,
      tournamentRounds: filters.tournamentRounds,
      teamHomeVenue: model.teamHomeVenue,
      teamCountryCodes: filters.teamCountryCodes,
      hideSoldOut: !!filters.hideSoldOut || !!model.availableFilters?.hideSoldOutDefaultOn,
      calendarDayGroups: getPerformerResidencyContext(model.performerGridSurface)?.calendarDayGroups ?? null,
      performerGridSurface: model.performerGridSurface,
    } as const satisfies Parameters<typeof useGridsReducer>[0];
  }, [
    dates,
    f1FilterDefault,
    filters.country,
    filters.tour,
    filters.eventBooleanFilters,
    filters.eventViewType,
    filters.gridFilterType,
    filters.homeAwayFilterType,
    filters.opponentCategoryId,
    filters.sortBy,
    filters.venueIdFilterType,
    model.seoCity,
    location,
    model.categorySummary.categoryId,
    model.paidSearchQueryCity,
    model.paidSearchQueryVenueId,
    recentlyViewedEventsArray,
    filters.passLengthType,
    filters.timeOfDayType,
    model.pinnedEvent,
    filters.medianPriceBucketFilter,
    filters.tournamentRound,
    filters.tournamentRounds,
    model.teamHomeVenue,
    overrideOpponentCategoryId,
    filters.teamCountryCodes,
    filters.hideSoldOut,
    model.availableFilters?.hideSoldOutDefaultOn,
    model.performerGridSurface,
  ]);
  const gridsContext = useGridsReducer(reducerOptions);

  const PerformerHeader = useMemo(
    () => <Header isUserLoggedIn={appContext.isUserLoggedIn} isNativeApp={appContext.isNativeApp} />,
    [appContext.isUserLoggedIn, appContext.isNativeApp],
  );

  const PerformerBody = useMemo(
    () => <Body userHasVisitedBefore={userHasVisitedBefore} isReturningGoogleUser={isReturningGoogleUser} />,
    [userHasVisitedBefore, isReturningGoogleUser],
  );

  const PerformerFooter = useMemo(
    () => (
      <LazyFooter
        companyLinks={model.footer.companyLinks}
        supportLinks={model.footer.supportLinks}
        getLocationSettingsUrl={model.footer.getLocationSettingsUrl}
      />
    ),
    [model.footer],
  );

  const heroBannerFavoritesDataContext = useMemo(
    () => ({
      entityType: ENTITY_TYPES.CATEGORY,
      entityId: model.categorySummary.categoryId,
    }),
    [model.categorySummary.categoryId],
  );

  useEffect(() => {
    for (const { gridType, slot } of getDisplayedPerformerGridEntries(model.performerGridSurface)) {
      const activeEventCount = slot.items?.filter((event) => event.hasActiveListings).length;

      if (activeEventCount === 0)
        storePageEvent(PE_TYPE.LOGGING, PE_KEYS.PERFORMER_NO_ACTIVE_EVENTS_GRID, gridType.toString());
    }
  }, [model.performerGridSurface, storePageEvent]);

  useEffect(() => {
    if (!isConfirmLocationModalNotVisible) {
      return;
    }

    if (!model.categorySummary.categoryId) {
      return;
    }

    sendPerformerLandingUAE({
      appContext,
      env,
      model,
      location,
      locationOverride,
    });
  }, [
    appContext,
    env,
    isConfirmLocationModalNotVisible,
    model.categorySummary.categoryId,
    model.categorySummary.categoryName,
    location,
    locationOverride,
    model.locationOverride,
    model.ipLocation,
    model.seoCity,
    model.paidSearchQueryCity,
    model.performerGridSurface,
    model.totalUnfilteredEventsCount,
    model.dynamicLocalGridRadiusInMeters,
    model,
  ]);

  // Location change detection for UAE tracking
  useEffect(() => {
    if (!isConfirmLocationModalNotVisible || !model.categorySummary.categoryId) {
      return;
    }

    if (!hasInitialLocationBeenSet.current && location) {
      previousLocationRef.current = location;
      hasInitialLocationBeenSet.current = true;
      return;
    }

    if (!previousLocationRef.current || !location) {
      return;
    }

    if (previousLocationRef.current.lt === location.lt && previousLocationRef.current.lg === location.lg) {
      return;
    }

    sendLocationChangeUAE({
      appContext,
      env,
      prevLocation: previousLocationRef.current,
      location,
      model,
    });

    previousLocationRef.current = location;
  }, [appContext, env, isConfirmLocationModalNotVisible, location, model]);

  return (
    <div>
      <AddFeature id={FeatureSetEnum.TMPostHogSessionReplayPOC} feature={() => <PostHogSessionReplay />} />
      <EventsGridContext.Provider value={gridsContext}>
        <LocationContext.Provider value={locationContext}>
          <DateContext.Provider value={datesContext}>
            <FavoritesProvider data={resolveInitialFavoritesStateFromPerformerPageModel(model)}>
              <HeroBannerFavoritesDataContext.Provider value={heroBannerFavoritesDataContext}>
                <Feature
                  id={FeatureSetEnum.GlobalNewExperimentHoldoutAAFix}
                  control={() => null}
                  variant={() => null}
                />
                {aipBottomsheet && <AipBottomsheetFeature state={aipBottomsheet} />}
                {model.showSthRelistMerchBanner && <STHRelistBanner leagueId={model.leagueId} />}
                <PerformerLayout header={PerformerHeader} body={PerformerBody} footer={PerformerFooter} />
              </HeroBannerFavoritesDataContext.Provider>
            </FavoritesProvider>
          </DateContext.Provider>
        </LocationContext.Provider>
      </EventsGridContext.Provider>
    </div>
  );
};
